import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ChatInterface from '../components/ChatInterface';
import { useAgent } from '../context/AgentContext';
import { useZone } from '../context/ZoneContext';
import Button from '../components/Button';
import Alert from '../components/Alert';

const AskAgentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  padding: 1.5rem;
`;


const ZoneSelector = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const ZoneLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-right: 0.75rem;
`;

const ZoneSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border.main};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary.light};
  }
`;

const AskAgent = () => {
  const { state, sendMessage, clearChat } = useAgent();
  const { messages, isTyping, suggestions, error, loading } = state;
  const { state: zoneState } = useZone();
  const { zones } = zoneState;
  
  const [selectedZone, setSelectedZone] = useState('all');
  const [zoneFilteredSuggestions, setZoneFilteredSuggestions] = useState([]);
  
  // Filter suggestions based on selected zone
  useEffect(() => {
    if (selectedZone === 'all') {
      setZoneFilteredSuggestions(suggestions);
    } else {
      // In a real app, we would filter suggestions based on zone
      // For now, just use all suggestions
      setZoneFilteredSuggestions(suggestions);
    }
  }, [selectedZone, suggestions]);
  
  // Handle sending a message
  const handleSendMessage = async (message) => {
    try {
      await sendMessage(message);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };
  
  // Handle clearing chat history
  const handleClearChat = async () => {
    if (window.confirm('Are you sure you want to clear the chat history?')) {
      await clearChat();
    }
  };
  
  return (
    <AskAgentContainer>
      <div>
        <ZoneSelector>
          <ZoneLabel>Filter by zone:</ZoneLabel>
          <ZoneSelect 
            value={selectedZone} 
            onChange={(e) => setSelectedZone(e.target.value)}
          >
            <option value="all">All Zones</option>
            {zones && zones.map(zone => (
              <option key={zone.id} value={zone.id}>{zone.name}</option>
            ))}
          </ZoneSelect>
        </ZoneSelector>
        
        {error && (
          <Alert 
            type="error" 
            message={error} 
            dismissible 
            style={{ marginBottom: '1rem' }}
          />
        )}
        
        <ChatInterface 
          messages={messages}
          isTyping={isTyping}
          suggestions={zoneFilteredSuggestions}
          loading={loading}
          error={error}
          onSendMessage={handleSendMessage}
        />
        
        <div style={{ marginTop: '1rem', textAlign: 'right' }}>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleClearChat}
          >
            Clear Chat History
          </Button>
        </div>
      </div>
    </AskAgentContainer>
  );
};

export default AskAgent;
